"use client";

import Breadcrumb from "@/components/Common/Breadcrumb";
import { getImagePath } from "@/lib/utils";
import { useTranslations } from "next-intl";

const Franchise = () => {
  const t = useTranslations("Pricing");

  // Lines retired by the Own & Earn update are gone from en/mr/hi; the other
  // languages still have them, so each one renders only if its key exists.
  const priceIcons = [
    ["/images/price/icon.png", "payAsYouWatch.points.one"],
    ["/images/price/icon-2.png", "payAsYouWatch.points.two"],
    ["/images/price/icon-3.png", "payAsYouWatch.points.three"],
    ["/images/price/icon-4.png", "payAsYouWatch.points.four"],
  ].filter(([, key]) => t.has(key));
  const steps = ["step1", "step2", "step3"].filter((k) => t.has(`howItWorks.${k}`));
  const highlights = ["h1", "h2", "h3", "h4"].filter((k) => t.has(`highlights.${k}`));
  const mdCols = { 1: "md:grid-cols-1", 2: "md:grid-cols-2", 3: "md:grid-cols-3", 4: "md:grid-cols-4" };
  const compareRows = (t.has("compare.rows") ? t.raw("compare.rows") : []) as CompareRow[];

  return (
    <section className="section-padding bg-white py-24">
      <Breadcrumb
        pageName={t("breadcrumb.pageName")}
        description={t("breadcrumb.description")}
        bgImages={[getImagePath("/images/breadcrumb/aboutUs.webp")]}
        bgIndex={0}
      />

      <div className="container mx-auto md:px-6 py-10">
        <div className="leading-relaxed w-full">
          <h2 className="text-primary text-3xl">{t("hero.title")}</h2>
          <p>{t("hero.subtitle")}</p>
        </div>

        {/* Own vs Rent comparison */}
        {compareRows.length > 0 && (
          <div className="w-full mt-8">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[560px] border-collapse text-left text-base md:text-lg">
                <thead>
                  <tr className="bg-primary text-white">
                    <th className="p-3 md:p-4"></th>
                    <th className="p-3 md:p-4 font-semibold">{t("compare.own")}</th>
                    <th className="p-3 md:p-4 font-semibold">{t("compare.rent")}</th>
                  </tr>
                </thead>
                <tbody>
                  {compareRows.map((row, i) => (
                    <tr key={i} className="border-b border-gray-200 even:bg-gray-50">
                      <th className="p-3 md:p-4 font-semibold text-primary align-top">{row.label}</th>
                      <td className="p-3 md:p-4 align-top">{row.own}</td>
                      <td className="p-3 md:p-4 align-top">{row.rent}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-4">{t("compare.note")}</p>
          </div>
        )}

        {/* Pay As You Watch */}
        <div className="w-full flex flex-col md:flex-row gap-10 md:gap-20 items-center justify-center mt-5 ">
          <div className="w-full mt-5">
            <h3 className="text-primary text-2xl mb-4">
              {t("payAsYouWatch.title")}
            </h3>

            <div className={`grid grid-cols-1 ${mdCols[priceIcons.length]} gap-5 text-base md:text-lg`}>
              {priceIcons.map(([icon, key]) => (
                <PriceIcon key={key} icon={icon} text={t(key)} />
              ))}
            </div>
          </div>
        </div>

        {/* Special Packages */}
        {t.has("specialPackages.title") && (
        <div className="w-full mt-5 mb-5 bg-price px-5 md:px-10 py-10 text-white rounded-tl-[80px] rounded-br-[80px]">
          <h3 className="text-2xl md:text-3xl mb-4 ">
            {t("specialPackages.title")}
          </h3>
          <ul className="list-disc pl-5 space-y-3 text-white">
            <li className="text-white">{t("specialPackages.list1")}</li>
            <li className="text-white">{t("specialPackages.list2")}</li>
          </ul>
        </div>
        )}

        {/* How It Works */}
        <div className="leading-relaxed w-full mt-14">
          <h2 className="text-primary text-3xl">{t("howItWorks.title")}</h2>
          <ul className="list-disc pl-5 space-y-3 mt-3">
            {steps.map((k) => (
              <li key={k}>{t(`howItWorks.${k}`)}</li>
            ))}
          </ul>
        </div>

        {/* Highlights */}
        <div className={`grid grid-cols-1 ${mdCols[highlights.length]} gap-2 mt-10 w-full text-left mx-auto md:py-2 md:px-2  `}>
          {highlights.map((k) => (
            <Quote key={k} text={t(`highlights.${k}`)} />
          ))}
        </div>

        {/* Footer */}
        <div className="leading-relaxed w-full mt-14">
          {t.has("footer.title") && (
            <h2 className="text-primary text-2xl px-2 break-words">
              {t("footer.title")}
            </h2>
          )}
          <p className="font-bold mt-3 text-lg">{t("footer.tagline")}</p>
        </div>
      </div>
    </section>
  );
};

type CompareRow = { label: string; own: string; rent: string };

const PriceIcon = ({ icon, text }) => (
  <div className="flex items-start mb-3">
    <img
      src={getImagePath(icon)}
      alt="check-icon"
      className="inline-block w-16 h-16 mr-2"
    />
    <p>{text}</p>
  </div>
);

const Quote = ({ text }) => (
  <div className="quote-box mt-5 p-4 border-l-4 border-primary ">
    <p className="font-open-sans font-normal">{text}</p>
  </div>
);

export default Franchise;
